import { Box, Link, Typography, useMediaQuery, useTheme } from '@mui/material';

import FocalPointImage from '@/components/FocalPointImage';
import { BlogAuthorAuthorBioSummary, ContentTypeRichTextContent, Maybe } from '@/types/generated';
import ContentTypeRichText from '@/components/ContentTypeRichText';

import {
	blogAuthorCtrStyles,
	blogAuthorImageCtrStyles,
	blogAuthorImageNameCtrStyles,
	blogAuthorImageStyles,
	blogAuthorNameStyles,
	blogAuthorSectionHeaderStyles,
	blogAuthorSummaryStyles,
} from './BlogDetailsPage.styles';
import { BlogImage } from './BlogDetailsPage';

export interface IBlogAuthorProps {
	/**
	 * Required field for the BlogDetailsPage Author
	 *
	 * @type {string}
	 * @memberof IBlogAuthorProps
	 */
	name: string;
	/**
	 * Optional field for the BlogDetailsPage Author Title
	 *
	 * @type {string}
	 * @memberof IBlogAuthorProps
	 */
	title?: string | null;
	/**
	 * Optional field for the BlogDetailsPage Author Image
	 *
	 * @type {Array<IBlogDetailPageImage>;}
	 * @memberof IBlogDetailsPage
	 */
	image?: BlogImage | null;
	/**
	 * Optional richtext field for the BlogDetailsPage Author Bio Summary
	 *
	 * @type {Maybe<BlogAuthorAuthorBioSummary>}
	 * @memberof IBlogAuthorProps
	 */
	authorBioSummary?: Maybe<BlogAuthorAuthorBioSummary>;
	/**
	 * Optional URL path to the author's "More About the Author" page.
	 * When provided, the author name will render as a link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IBlogAuthorProps
	 */
	moreAboutTheAuthorPagePath?: Maybe<string>;
	/**
	 * Optional label for the author section heading (e.g. "Written by", "Medically reviewed by").
	 * Defaults to "More About the Author".
	 *
	 * @type {string}
	 * @memberof IBlogAuthorProps
	 */
	sectionLabel?: string;
}
export default function BlogAuthorDetails({
	name,
	title,
	image,
	authorBioSummary,
	moreAboutTheAuthorPagePath,
	sectionLabel = 'More About the Author',
}: IBlogAuthorProps) {
	const theme = useTheme();
	const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

	const responsiveTypeVariant = isMobileScreen ? 'bodyMediumBook' : 'bodyLargeBook';

	// Validate URL to prevent XSS attacks - only allow relative paths or http/https URLs
	const isValidUrl = (url: string | null | undefined): boolean => {
		if (!url) return false;
		return /^(https?:\/\/|\/)/i.test(url);
	};

	const validAuthorPagePath = isValidUrl(moreAboutTheAuthorPagePath) ? moreAboutTheAuthorPagePath : null;

	return (
		<Box data-testid="blog_detail_page_author_container" sx={blogAuthorCtrStyles} component="section">
			<Typography
				data-testid="blog_detail_page_author_section_label"
				variant="overline"
				color="text.secondary"
				sx={blogAuthorSectionHeaderStyles}
			>
				{sectionLabel}
			</Typography>
			<Box sx={blogAuthorImageNameCtrStyles}>
				<Box sx={blogAuthorImageCtrStyles} data-testid="blog_detail_page_author_focal_point_image_container">
					{image && (
						<FocalPointImage
							brandFolderImage={image.image}
							focalPoint={image.focalPoint}
							dataTestId="blog_detail_page_author_focal_point_image"
							imageSx={blogAuthorImageStyles}
						/>
					)}
				</Box>
				<Box sx={blogAuthorNameStyles}>
					{(() => {
						const authorNameTypography = (
							<Typography
								data-testid="blog_detail_page_author_name"
								variant="header3"
								color={validAuthorPagePath ? 'text.interactive' : 'text.primary'}
								component="span"
							>
								{name}
							</Typography>
						);

						return validAuthorPagePath ? (
							<Link
								href={validAuthorPagePath}
								data-testid="blog_detail_page_author_profile_link"
								underline="hover"
								color="inherit"
							>
								{authorNameTypography}
							</Link>
						) : (
							authorNameTypography
						);
					})()}
					{title && title !== '' && (
						<Typography data-testid="blog_detail_page_author_title" variant="overline" color="text.primary">
							{title}
						</Typography>
					)}
				</Box>
			</Box>
			{authorBioSummary?.json && (
				<ContentTypeRichText
					content={authorBioSummary as ContentTypeRichTextContent}
					dataTestId="blog_detail_page_author_summary_richtext"
					contentMaxWidth="100%"
					disableGutters={true}
					disablePaddingOn={['Top', 'Bottom']}
					containerSx={blogAuthorSummaryStyles}
					docProps={{ variant: responsiveTypeVariant, color: 'text.secondary' }}
				/>
			)}
		</Box>
	);
}
